import React, { useRef, useState } from 'react';
import { Paper, Typography } from '@material-ui/core';

import Button from '~/components/Button';
import { checkIsSmallScreen } from '~/functions/ScreenSizeFunctions';
import { useSegmentTrack } from '~/functions/SegmentFunctions';
import TurboTenantFullLogo from '~/icons/TurboTenantFullLogo';
import { useStyles } from '~/views/RentPayments/styles';

const SEGMENT_LOCATION = 'Rent Payments Page';

export default function RentPayments() {
  const classes = useStyles();
  const accountingSectionRef = useRef(null);
  const tracking = useSegmentTrack();
  const isMobile = checkIsSmallScreen();
  const [shouldDisplayVideo, setShouldDisplayVideo] = useState(false);

  const handleScrollToAccountingSection = () => {
    accountingSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={classes.root}>
      <div className={classes.upperContainerRoot}>
        <div className={classes.upperContainerLeft}>
          <div className={classes.upperContainerText}>
            <Typography variant="h4" gutterBottom>
              Automate Rent Collection, Track It in REI Hub
            </Typography>
            <Typography variant="body2">
              Our friends at TurboTenant make rent collection effortless. The best part? Payments sync directly into
              your REI Hub account.{' '}
              <span
                className={classes.textLink}
                role="button"
                tabIndex="0"
                onClick={() => {
                  tracking('see_how_this_works clicked', { location: SEGMENT_LOCATION });
                  handleScrollToAccountingSection();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    tracking('see_how_this_works clicked', { location: SEGMENT_LOCATION });
                    handleScrollToAccountingSection();
                  }
                }}
              >
                {' '}
                See how this works
              </span>
              .
            </Typography>
          </div>
          <Button
            variant="contained"
            color="primary"
            size="large"
            className={classes.getStartedButton}
            onClick={() => {
              tracking('get_started clicked', { location: SEGMENT_LOCATION });
              window.location.replace('https://form.typeform.com/to/vuUfkyul');
            }}
          >
            Get Started
          </Button>
        </div>
        <div className={classes.marginRight}>
          <video src="/rp received payment-_6.mp4" autoPlay loop muted className={classes.video} />
        </div>
      </div>
      <div className={classes.cardRowContainer}>
        <Paper elevation={1} className={classes.card}>
          <img src="/Frame.svg" alt="" className={classes.quotationMark} />
          <div className={classes.cardContentContainer}>
            <Typography variant="caption" color="textPrimary">
              Convenient and easy to use for both renters and landlords!
            </Typography>
            <div className={classes.cardLowerTextContainer}>
              <Typography variant="caption" className={classes.nameText}>
                Marqus
              </Typography>
              <Typography variant="caption" className={classes.landlordText}>
                1 property landlord
              </Typography>
            </div>
          </div>
        </Paper>
        <Paper elevation={1} className={classes.card}>
          <img src="/Frame.svg" alt="" className={classes.quotationMark} />
          <div className={classes.cardContentContainer}>
            <Typography variant="caption" color="textPrimary">
              I like the automatic receipts, not having to deposit a check, and being able to track everything in one
              place.
            </Typography>
            <div className={classes.cardLowerTextContainer}>
              <Typography variant="caption" className={classes.nameText}>
                Ryan
              </Typography>
              <Typography variant="caption" className={classes.landlordText}>
                3 property landlord
              </Typography>
            </div>
          </div>
        </Paper>
        <Paper elevation={1} className={classes.card}>
          <img src="/Frame.svg" alt="" className={classes.quotationMark} />
          <div className={classes.cardContentContainer}>
            <Typography variant="caption" color="textPrimary">
              The best things are that it's free and it sends automatic rent reminders. It is simple, and tenants pay
              more timely!
            </Typography>
            <div className={classes.cardLowerTextContainer}>
              <Typography variant="caption" className={classes.nameText}>
                Jacob
              </Typography>
              <Typography variant="caption" className={classes.landlordText}>
                8 property landlord
              </Typography>
            </div>
          </div>
        </Paper>
      </div>
      <div className={classes.purpleSectionContainer}>
        <div className={classes.purpleSectionText}>
          <TurboTenantFullLogo color="white" />
          <Typography variant="h4">Your one-stop shop for finding and managing tenants</Typography>
          <Typography variant="body2">
            Whether you have one or 100 doors, TurboTenant, the parent company of REI Hub, is the intuitive toolset you
            need to streamline your entire rental process. And it's for free!
          </Typography>
        </div>
        <div className={classes.purpleSectionCardRowContainer}>
          <div className={classes.purpleSectionCard}>
            <img src="/icons/yard_sign.svg" alt="" className={classes.purpleSectionCardIcon} />
            <Typography variant="h6">Rental Advertising</Typography>
            <Typography variant="caption" color="textPrimary">
              Build a listing in less than 10 minutes, then push it across the web to collect high-intent leads.
            </Typography>
          </div>
          <div className={classes.purpleSectionCard}>
            <img src="/icons/verify.svg" alt="" className={classes.purpleSectionCardIcon} />
            <Typography variant="h6">Apps & Screening</Typography>
            <Typography variant="caption" color="textPrimary">
              Gain insight into potential tenants' rental, financial, criminal, and eviction history — for $0 on your
              end.
            </Typography>
          </div>
          <div className={classes.purpleSectionCard}>
            <img src="/icons/lease.svg" alt="" className={classes.purpleSectionCardIcon} />
            <Typography variant="h6">Lease Agreements</Typography>
            <Typography variant="caption" color="textPrimary">
              Build and adjust a lease on your own — specific to your state's laws — in less than 15 minutes.
            </Typography>
          </div>
          <div className={classes.purpleSectionCard}>
            <img src="/icons/receiving_money.svg" alt="" className={classes.purpleSectionCardIcon} />
            <Typography variant="h6">Rent Payments</Typography>
            <Typography variant="caption" color="textPrimary">
              Get easy tracking, autopay, automatic reminders, custom late fees, and receipts - free for landlords.
            </Typography>
          </div>
        </div>
        <div ref={accountingSectionRef} />
      </div>
      <div className={classes.accountingSectionContainer}>
        <div className={classes.accountingSectionContent}>
          <div className={classes.videoRoot}>
            <div className={classes.videoWrapper}>
              {shouldDisplayVideo ? (
                <iframe
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  src="https://www.youtube.com/embed/1gn3ciLLD-Q?autoplay=1"
                  className={classes.iframe}
                  data-embed="true"
                  title="rent payments video"
                />
              ) : (
                <input
                  type="image"
                  src="/rent-payments-video-thumbnail.png"
                  alt="Rent Payments Video"
                  onClick={() => setShouldDisplayVideo(true)}
                  className={classes.iframe}
                />
              )}
            </div>
          </div>
          <div className={classes.accountingSectionLeftContainer}>
            <Typography variant="h5">
              Accounting built specifically for your
              <span className={classes.purpleText}> rental business.</span>
            </Typography>
            <div className={classes.accountingSectionCard}>
              <img src="/icons/pre-screener.svg" alt="" className={classes.accountingSectionCardIcon} />
              <div className={classes.accountingSectionCardText}>
                <Typography variant="subtitle2">Effortless Automated Collection</Typography>
                <Typography className={classes.captionSmall}>
                  Set automatic rent reminders, late fees, and instant payment receipts—no more chasing tenants or
                  manual follow-ups.
                </Typography>
              </div>
            </div>
            <div className={classes.accountingSectionCard}>
              <img src="/icons/expenses.svg" alt="" className={classes.accountingSectionCardIcon} />
              <div className={classes.accountingSectionCardText}>
                <Typography variant="subtitle2">All Payments Tracked in One Place</Typography>
                <Typography className={classes.captionSmall}>
                  Integrated with REI Hub to sync payments automatically, giving you clear financial visibility without
                  extra data entry.
                </Typography>
              </div>
            </div>
            <div className={classes.accountingSectionCard}>
              <img src="/icons/receiving_money.svg" alt="" className={classes.accountingSectionCardIcon} />
              <div className={classes.accountingSectionCardText}>
                <Typography variant="subtitle2">Tenants Can Pay How They Want with Autopay</Typography>
                <Typography className={classes.captionSmall}>
                  Let tenants pay their way—ACH, card, cash, check, or third-party apps—and schedule autopay ensuring
                  rent arrives on time, consistently.
                </Typography>
              </div>
            </div>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                tracking('learn_more clicked', { location: SEGMENT_LOCATION });
                window.location.replace('https://form.typeform.com/to/vuUfkyul');
              }}
            >
              Learn More
            </Button>
          </div>
        </div>
      </div>
      {isMobile ? null : (
        <div className={classes.paymentsSectionContainer}>
          <Typography variant="h4">
            Collect payments with a toolset
            <span className={classes.purpleText}> built for landlords.</span>
          </Typography>
          <img src="/tool-comparison-card.svg" alt="Tool Comparison Card" />
        </div>
      )}
    </div>
  );
}
